import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { CircleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Label + control + hint + error, as one thing.
 *
 * Assembling these by hand at each call site is how a field ends up with a
 * label that is not wired to its input, or an error a screen reader never
 * hears. The ids are generated here and the wiring is not optional.
 */

export function Label({ className, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      className={cn(
        'text-sm font-medium text-ink',
        'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

const control = [
  'w-full min-w-0 rounded-lg border bg-raised text-ink',
  'border-line-strong placeholder:text-ink-subtle',
  'transition-[border-color,box-shadow] duration-[var(--motion-duration-fast)] ease-standard',
  'hover:border-line-bold',
  'focus:border-line-brand focus:outline-none focus:ring-2 focus:ring-ring/25',
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-sunken',
  'aria-[invalid=true]:border-error-line aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-error-ink/20',
].join(' ');

export type InputProps = React.ComponentProps<'input'> & { invalid?: boolean };

export function Input({ className, invalid, ...props }: InputProps) {
  return (
    <input
      // An explicit height from the size scale, not only a minimum. In a grid
      // row a stretched neighbour would otherwise make two identical fields
      // visibly different heights.
      className={cn(control, 'h-control-lg px-4 text-base', className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

export function Textarea({
  className,
  invalid,
  ...props
}: React.ComponentProps<'textarea'> & { invalid?: boolean }) {
  return (
    <textarea
      className={cn(control, 'min-h-24 px-4 py-3 text-base leading-normal', className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

type FieldProps = {
  label: string;
  hint?: React.ReactNode;
  error?: string | null;
  /** Visually hides the label while leaving it for a screen reader. */
  hideLabel?: boolean;
  className?: string;
  children: (ids: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': true | undefined;
  }) => React.ReactNode;
};

export function Field({ label, hint, error, hideLabel, className, children }: FieldProps) {
  const id = React.useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    /*
     * `content-start` so the control cannot be stretched by a taller sibling
     * in the same grid row. Two fields side by side where only one carries a
     * hint is the case that breaks, and it looks like a rendering bug.
     */
    <div className={cn('grid content-start gap-1.5', className)}>
      <Label htmlFor={id} className={hideLabel ? 'sr-only-text' : undefined}>
        {label}
      </Label>

      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}

      {/* The error comes FIRST in the DOM after the control, so it is the
          first thing announced when focus lands on an invalid field. */}
      {error ? (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-error-ink">
          <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : null}

      {hint ? (
        <p id={hintId} className="text-sm text-ink-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
